import { Box, Stack, Typography, useTheme } from "@mui/material";
import CustomTable from "global/components/CustomTable/CustomTable";
import {
  convertESTtoUserLocalDateAndTime,
  formatDateWithTimeInLongDateFormat,
  handleErrorMessage,
  initialPageFilterState,
  openWarningNotification,
  validatePageQuery,
  validatePerPageQuery,
} from "helpers/methods";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  fetchContactGroupsById,
  fetchContactGroupsCountById,
} from "../ViewContactService";
import { pageFilters } from "models/interfaces";
import { useLocation } from "react-router-dom";
import contactsStyles from "screens/Contacts/Contacts.styles";

interface CustomProps {
  contact: any;
}

const groupsTableHeader = [
  {
    name: "Group Name",
    field: "name",
    align: "left",
    sorting: false,
  },
  {
    name: "Contacts",
    field: "contacts",
    align: "left",
    sorting: false,
  },
  {
    name: "Created On",
    field: "createdOn",
    align: "left",
    sorting: false,
  },
  {
    name: "Updated On",
    field: "updatedOn",
    align: "left",
    sorting: false,
  },
  {
    name: "Type",
    field: "type",
    align: "left",
    sorting: false,
  },
];

const ContactGroups = (props: CustomProps) => {
  const theme = useTheme();
  const classes = contactsStyles(theme);
  const [groups, setGroups] = useState<string[]>([]);
  const [count, setCount] = useState<number>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const urlParams = new URLSearchParams(useLocation().search);
  const pageFromUrl = validatePageQuery(urlParams.get("page"));
  const rowsPerPageFromUrl = validatePerPageQuery(urlParams.get("rowsPerPage"));
  const searchFromUrl = urlParams.get("search");

  const [tagFilters, setTagFilters] = useState<pageFilters>(
    initialPageFilterState(pageFromUrl, rowsPerPageFromUrl, searchFromUrl ?? "")
  );

  useEffect(() => {
    const { page, rowsPerPage } = tagFilters;
    window.history.replaceState(
      null,
      "",
      `?page=${page}&rowsPerPage=${rowsPerPage}`
    );
    getTableData();
  }, [tagFilters]);

  const handleRowsPageData = (event: any) => {
    const rowsPerPage = event.target.value;
    setTagFilters({
      ...tagFilters,
      rowsPerPage,
      page: 1,
    });
  };

  const handleChangePage = async (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    setTagFilters({ ...tagFilters, page });
  };

  const getTableData = async () => {
    const { page, rowsPerPage } = tagFilters;
    try {
      setIsLoading(true);
      const count = await fetchContactGroupsCountById(props.contact.id);
      setCount(count);
      await getTableDataAccordingToPage(page, count, rowsPerPage);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTableDataAccordingToPage = async (
    page: number,
    count: number,
    rowsPerPage: number
  ) => {
    try {
      let groupsRes: any[] = [];
      groupsRes = await fetchContactGroupsById(
        props.contact.id,
        page,
        rowsPerPage
      );
      const tableData = convertDataToTableFormat(groupsRes);
      setGroups([...tableData]);
    } catch (error) {
      throw error;
    }
  };

  const convertDataToTableFormat = (data: any) => {
    return data.map((group: any) => {
      return {
        name: (
          <Typography sx={{ color: "#475569" }} variant="subtitle2">
            {group.name}
          </Typography>
        ),

        contacts: (
          <Typography sx={{ color: "#475569" }} variant="subtitle2">
            {group.recipientCount}
          </Typography>
        ),
        createdOn: (
          <Typography sx={{ color: "#475569" }} variant="subtitle2">
            {convertESTtoUserLocalDateAndTime(
              group.createdOn,
              "MMMM D, YYYY [at] h:mm A"
            )}
          </Typography>
        ),
        updatedOn: (
          <Typography sx={{ color: "#475569" }} variant="subtitle2">
            {convertESTtoUserLocalDateAndTime(
              group.updatedOn,
              "MMMM D, YYYY [at] h:mm A"
            )}
          </Typography>
        ),
        type: (
          <Typography
            sx={{
              color: group.public ? "#47AA12" : "#475569",
              display: "flex",
              alignItems: "center",
            }}
            variant="subtitle2"
          >
            <Box
              sx={{
                ...classes.contactStatusBox,
                backgroundColor: group.public ? "#47AA12" : "#475569",
              }}
            />
            {group.public ? "Public" : "Private"}
          </Typography>
        ),
      };
    });
  };

  const getContactGroups = () => {
    return (
      <Stack direction="column" spacing={2}>
        <CenteredCardLayout removeLastChildBottomPadding={true}>
          <Box>
            <CustomTable
              headers={groupsTableHeader}
              rows={groups}
              page={tagFilters.page}
              rowsPerPage={tagFilters.rowsPerPage}
              onRowsPerPageChange={handleRowsPageData}
              onPageChange={handleChangePage}
              count={count}
              showJumpToPageNumber={false}
              handleOnChangePageInput={handleChangePage}
              showPagination={true}
              isLoading={isLoading}
            />
          </Box>
        </CenteredCardLayout>
      </Stack>
    );
  };

  return <>{getContactGroups()}</>;
};

export default ContactGroups;
